import type { Dayjs } from 'dayjs'

import clsx from 'clsx'
import { FullLoader } from 'components/atoms/FullLoader/FullLoader.tsx'
import { useRef } from 'react'
import { useAppSelector } from 'shared/hooks'

import { api } from '../../api.ts'
import { useAutoSync, useBottomScrolling, useCurrentId, useReactOnSyncChange } from '../../hooks'
import { selectors } from '../../utils/selectors.ts'
import { formatChatDate } from '../../utils/utils.ts'
import { Message } from '../Message/Message.tsx'
import css from './Chat.module.css'
import { LoadPrevious } from './LoadPrevious.tsx'

const getId = (id: string, day?: Dayjs) => `${id}-${day?.unix()}`

export function Conversation({ className }: { className?: string }) {
	const scrollableRef = useRef<HTMLDivElement | null>(null)
	const conversation = useCurrentId()

	const { days, last, map } = useAppSelector(root =>
		selectors.conversation(root, conversation)
	)

	useBottomScrolling(scrollableRef, Boolean(last), [days, map, last])

	const pullingOptions = useAutoSync()

	const recentQuery = api.useMessagesQuery(conversation, {
		...pullingOptions,
		refetchOnMountOrArgChange: true,
		skip: !conversation
	})

	useReactOnSyncChange(Boolean(conversation) && recentQuery.refetch)

	const showLoader =
		recentQuery.isLoading ||
		(recentQuery.isFetching && !recentQuery.currentData)

	const showPrevCursor = recentQuery.currentData?.prevCursor

	// // biome-ignore lint/correctness/useExhaustiveDependencies: No need
	// useEffect(() => {
	// 	if (prevCursor && entry?.isIntersecting && !scrollId.current) {
	// 		scrollId.current = getId(conversation, days[0])

	// 		return () => {
	// 			setTimeout(() => {
	// 				document.getElementById(scrollId.current)?.scrollIntoView({
	// 					behavior: 'instant',
	// 					block: 'start',
	// 					inline: 'nearest'
	// 				})

	// 				scrollId.current = ''
	// 			}, 0)
	// 		}
	// 	}
	// }, [prevCursor, entry?.isIntersecting])

	return (
		<div
			className={clsx(css.chat, 'app-scroll', className)}
			id='chat'
			ref={scrollableRef}
		>
			{showLoader && <FullLoader size={40} />}
			{showPrevCursor && (
				<LoadPrevious cursor={showPrevCursor} scrollableRef={scrollableRef} />
			)}

			{days.map(day => {
				const key = day.toISOString()

				return (
					<section
						className={css.section}
						id={getId(conversation, day)}
						key={key}
					>
						<time className={css.date} dateTime={key}>
							{formatChatDate(day)}
						</time>
						<div className={css.messages}>
							{map[key]?.map(message => (
								<Message {...message} key={message.id} />
							))}
						</div>
					</section>
				)
			})}
		</div>
	)
}
